export interface NumberPadProps {
  value: string
  disabled?: boolean
  onChange: (next: string) => void
  onSubmit: () => void
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'enter'] as const

/**
 * As many digits as any answer in this game could need.
 *
 * Exported so the physical keyboard and the on-screen pad share one cap. They
 * used to have one each — four here and six there — which is two answers to
 * the same question waiting to disagree the first time a skill produces a
 * wider number.
 */
export const MAX_DIGITS = 4

/**
 * Digits, not the device keyboard.
 *
 * A phone keyboard opening for a maths answer brings autocorrect, a layout
 * that shifts the question off screen, and a comma key nobody wants. This is
 * eight big targets and nothing else.
 */
export function NumberPad({ value, disabled = false, onChange, onSubmit }: NumberPadProps) {
  const press = (key: (typeof KEYS)[number]) => {
    if (key === 'enter') return onSubmit()
    if (key === 'clear') return onChange('')
    if (value.length < MAX_DIGITS) onChange(value + key)
  }

  return (
    <div className="grid w-full max-w-xs grid-cols-3 gap-2" role="group" aria-label="Number pad">
      {KEYS.map((key) => (
        <button
          key={key}
          type="button"
          disabled={disabled || (key === 'enter' && value === '')}
          onClick={() => press(key)}
          aria-label={key === 'clear' ? 'Clear' : key === 'enter' ? 'Enter answer' : key}
          style={{ touchAction: 'manipulation' }}
          className={[
            'font-display flex min-h-16 items-center justify-center text-(length:--text-heading)',
            'rounded-(--radius-control) border-2 transition-colors disabled:opacity-40',
            key === 'enter'
              ? 'bg-primary text-on-primary border-transparent'
              : key === 'clear'
                ? 'bg-surface-sunken text-ink-muted border-border'
                : 'bg-surface-raised border-border hover:border-primary',
          ].join(' ')}
        >
          {key === 'clear' ? '←' : key === 'enter' ? '✓' : key}
        </button>
      ))}
    </div>
  )
}
